import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useRepoParams } from '../../hooks/useRepoParams';
import { refToSegment } from '../../utils/refSegment';
import { getTreeCached, type TreeItem, type TreeData } from '../../api/treeCache';
import { RepoCodeLayout } from '../../components/repo/RepoCodeLayout';
import './FileBrowser.css';

export function FileBrowserPage(props: { repoPath?: string; gitRef?: string; path?: string }) {
    const { owner, repo } = useRepoParams();
    const navigate = useNavigate();
    const params = useParams();

    // Override params with props if provided
    const ref = props.gitRef || params['*']?.split('/')[0] || 'main';
    const pathFromUrl = props.path !== undefined ? props.path : (params['*']?.split('/').slice(1).join('/') || '');

    const [currentPath, setCurrentPath] = useState(pathFromUrl);
    const [tree, setTree] = useState<TreeData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentRef] = useState(ref);

    const repoPath = props.repoPath || `${owner}/${repo}`;

    // 同步 URL 路径到状态
    useEffect(() => {
        setCurrentPath(pathFromUrl);
    }, [pathFromUrl]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getTreeCached(repoPath, currentRef, currentPath)
            .then((data) => {
                if (!cancelled) setTree(data);
            })
            .catch((err: any) => {
                if (!cancelled) setError(err.message || '加载文件树失败');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [repoPath, currentRef, currentPath]);

    const navigateTo = (item: TreeItem) => {
        if (item.type === 'tree') {
            setCurrentPath(item.path);
            navigate(`/${repoPath}/-/tree/${refToSegment(currentRef)}/${item.path}`);
        } else {
            navigate(`/${repoPath}/-/blob/${refToSegment(currentRef)}/${item.path}`);
        }
    };

    const navigateUp = () => {
        const parts = currentPath.split('/').filter(Boolean);
        parts.pop();
        const newPath = parts.join('/');
        setCurrentPath(newPath);
        navigate(`/${repoPath}/-/tree/${refToSegment(currentRef)}/${newPath}`);
    };

    const breadcrumbs = currentPath.split('/').filter(Boolean);

    return (
        <RepoCodeLayout
            repoPath={repoPath}
            refName={currentRef}
            treePath={currentPath}
        >
        <div className="file-browser">
            {/* 工具栏 */}
            <div className="browser-toolbar">
                <div className="branch-select">
                    <button className="branch-button">
                        <svg viewBox="0 0 16 16" width="16" height="16">
                            <path
                                fill="currentColor"
                                d="M9.5 3.25a2.25 2.25 0 1 1 3 2.122V6A2.5 2.5 0 0 1 10 8.5H6a1 1 0 0 0-1 1v1.128a2.251 2.251 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.5 0v1.836A2.493 2.493 0 0 1 6 7h4a1 1 0 0 0 1-1v-.628A2.25 2.25 0 0 1 9.5 3.25Z"
                            />
                        </svg>
                        {currentRef}
                        <svg viewBox="0 0 16 16" width="12" height="12">
                            <path
                                fill="currentColor"
                                d="m4.427 7.427 3.396 3.396a.25.25 0 0 0 .354 0l3.396-3.396A.25.25 0 0 0 11.396 7H4.604a.25.25 0 0 0-.177.427Z"
                            />
                        </svg>
                    </button>
                </div>

                {/* 面包屑 */}
                <div className="breadcrumbs">
                    <Link to={`/${repoPath}/-/tree/${refToSegment(currentRef)}`} className="breadcrumb-link">
                        {repo}
                    </Link>
                    {breadcrumbs.map((part, index) => (
                        <span key={index}>
                            <span className="separator">/</span>
                            <Link
                                to={`/${repoPath}/-/tree/${refToSegment(currentRef)}/${breadcrumbs.slice(0, index + 1).join('/')}`}
                                className="breadcrumb-link"
                            >
                                {part}
                            </Link>
                        </span>
                    ))}
                </div>

                <div className="toolbar-actions">
                    <Link to={`/${repoPath}/-/commits/${refToSegment(currentRef)}`} className="toolbar-btn">
                        <svg viewBox="0 0 16 16" width="16" height="16">
                            <path
                                fill="currentColor"
                                d="M1.643 3.143.427 1.927A.25.25 0 0 1 .427 1.573l1.216-1.216a.25.25 0 0 1 .426.177V1.75h7.065a.75.75 0 0 1 .654.387l1.05 1.851a.75.75 0 0 1-.653 1.112H2.069V6.286a.25.25 0 0 1-.427.177l-1.143-1.143a.75.75 0 0 1 0-.177V3.143Zm14.715 9.714 1.216 1.216a.25.25 0 0 1 0 .354l-1.216 1.216a.25.25 0 0 1-.426-.177V14.25H8.867a.75.75 0 0 1-.654-.387l-1.05-1.851a.75.75 0 0 1 .653-1.112h7.117V9.714a.25.25 0 0 1 .426-.177l1.143 1.143a.75.75 0 0 1 0 .177h-.144Z"
                            />
                        </svg>
                        历史
                    </Link>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading ? (
                <div className="loading">加载中...</div>
            ) : !tree ? (
                <div className="empty-state">
                    <h3>仓库为空</h3>
                    <p>这个仓库还没有任何内容</p>
                </div>
            ) : (
                <div className="file-list">
                    {/* 返回上级 */}
                    {currentPath && (
                        <div className="file-item" onClick={navigateUp}>
                            <div className="file-icon">
                                <svg viewBox="0 0 16 16" width="16" height="16">
                                    <path
                                        fill="currentColor"
                                        d="M1.5 3.25a1.25 1.25 0 0 1 1.25-1.25h2.146a1.25 1.25 0 0 1 .884.366l.846.846H12.5a1.25 1.25 0 0 1 1.25 1.25v7.538a.75.75 0 0 1-1.5 0V4.463l-2.5.002h-.086l-.086-.001-.707-.707a.25.25 0 0 0-.177-.073H2.75a.25.25 0 0 0-.25.25v8.131a.75.75 0 0 1-1.5 0V3.25Z"
                                    />
                                </svg>
                            </div>
                            <span className="file-name">..</span>
                        </div>
                    )}

                    {/* 文件列表 */}
                    {tree.items.map((item) => (
                        <div key={item.path} className="file-item" onClick={() => navigateTo(item)}>
                            <div className={`file-icon ${item.type}`}>
                                {item.type === 'tree' ? (
                                    <svg viewBox="0 0 16 16" width="16" height="16">
                                        <path
                                            fill="currentColor"
                                            d="M1.75 1A1.75 1.75 0 0 0 0 2.75v10.5C0 14.216.784 15 1.75 15h12.5A1.75 1.75 0 0 0 16 13.25v-8.5A1.75 1.75 0 0 0 14.25 3H7.5a.25.25 0 0 1-.2-.1l-.9-1.2C6.07 1.26 5.55 1 5 1H1.75Z"
                                        />
                                    </svg>
                                ) : (
                                    <svg viewBox="0 0 16 16" width="16" height="16">
                                        <path
                                            fill="currentColor"
                                            d="M2 1.75C2 .784 2.784 0 3.75 0h6.586c.464 0 .909.184 1.237.513l2.914 2.914c.329.328.513.773.513 1.237v9.586A1.75 1.75 0 0 1 13.25 16h-9.5A1.75 1.75 0 0 1 2 14.25Zm1.75-.25a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h9.5a.25.25 0 0 0 .25-.25V4.664a.25.25 0 0 0-.073-.177l-2.914-2.914a.25.25 0 0 0-.177-.073Z"
                                        />
                                    </svg>
                                )}
                            </div>
                            <span className="file-name">{item.name}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
        </RepoCodeLayout>
    );
}

export default FileBrowserPage;
